import { useEffect, useState } from "react";
import { ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, BarChart, Cell } from "recharts";
import { api, eur, pct, CATEGORY_COLORS } from "@/lib/api";
import { Stat } from "@/components/Badges";
import { t, locale } from "@/lib/i18n";

const tip = { background: "#221F1C", border: "1px solid #2E2A26", borderRadius: 8, fontSize: 12 };

export const KpiDashboard = () => {
  const [records, setRecords] = useState([]);
  const [menu, setMenu] = useState(null);
  useEffect(() => { api.get("/foodcost/records").then((r) => setRecords(r.data)); api.get("/dishes").then((r) => setMenu(r.data)); }, []);
  const last = records[records.length - 1];
  const totRev = records.reduce((a, r) => a + r.revenue, 0);
  const totCost = records.reduce((a, r) => a + r.actual_cost, 0);
  const totCovers = records.reduce((a, r) => a + r.covers, 0);

  return (
    <div data-testid="kpi-dashboard" className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat testId="kpi-total-fc" label={t("Food cost cumulato")} value={totRev ? pct((totCost / totRev) * 100) : "—"} hint={`${records.length} ${t("periodi")}`} />
        <Stat testId="kpi-total-margin" label={t("Margine lordo cumulato")} value={eur(totRev - totCost)} hint={t("su {v} ricavi", { v: eur(totRev) })} />
        <Stat testId="kpi-avg-cover" label={t("Ricavo medio / coperto")} value={totCovers ? eur(totRev / totCovers) : "—"} hint={`${totCovers.toLocaleString(locale())} ${t("coperti")}`} />
        <Stat testId="kpi-menu-cm" label={t("Margine menù totale")} value={menu ? eur(menu.total_cm) : "—"} hint={menu ? `${menu.total_sold} ${t("porzioni vendute")}` : ""} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="surface p-4">
          <div className="eyebrow mb-3">{t("Food cost % · preventivo vs consuntivo vs target")}</div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={records}>
                <CartesianGrid stroke="#2E2A26" vertical={false} />
                <XAxis dataKey="period" stroke="#6E675F" tick={{ fontSize: 11 }} />
                <YAxis stroke="#6E675F" tick={{ fontSize: 11 }} unit="%" domain={[0, "auto"]} />
                <Tooltip contentStyle={tip} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="budget_pct" name="Preventivo" fill="#3B82F6" radius={[3, 3, 0, 0]} />
                <Bar dataKey="actual_pct" name="Consuntivo" fill="#E67E22" radius={[3, 3, 0, 0]} />
                <Line dataKey="target_pct" name="Target" stroke="#10B981" strokeDasharray="4 4" dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="surface p-4">
          <div className="eyebrow mb-3">{t("Margine per coperto (€)")}</div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={records}>
                <CartesianGrid stroke="#2E2A26" vertical={false} />
                <XAxis dataKey="period" stroke="#6E675F" tick={{ fontSize: 11 }} />
                <YAxis stroke="#6E675F" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={tip} formatter={(v) => eur(v)} />
                <Bar dataKey="margin_per_cover" name="Margine / coperto" fill="#D4AF37" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {menu && (
          <div className="surface p-4 lg:col-span-2">
            <div className="eyebrow mb-3">{t("Performance per piatto · margine di contribuzione totale")}</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[...menu.dishes].sort((a, b) => b.total_cm - a.total_cm)} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid stroke="#2E2A26" horizontal={false} />
                  <XAxis type="number" stroke="#6E675F" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" stroke="#6E675F" tick={{ fontSize: 11 }} width={160} />
                  <Tooltip contentStyle={tip} formatter={(v) => eur(v)} />
                  <Bar dataKey="total_cm" name="Margine totale" radius={[0, 3, 3, 0]}>{menu.dishes.map((d) => <Cell key={d.id} fill={CATEGORY_COLORS[d.category]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
      {last && <p className="text-xs text-muted-foreground">Ultimo periodo «{last.period}»: efficienza di cucina misurata come scostamento consuntivo/preventivo = {last.variance_pts > 0 ? "+" : ""}{last.variance_pts} punti.</p>}
    </div>
  );
};
